import * as yup from "yup";
import dayjs, { Dayjs } from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";

import { TFunction } from "core/i18n";
import { iban } from "core/util";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

yup.addMethod<yup.StringSchema>(yup.string, "validBirthNumber", function (t: TFunction) {
    return this.test("validBirthNumber", t("error.notValid"), (value) => {
        if (!value) {
            return undefined;
        }
        if (value.length < 9) {
            return false;
        }

        const birthNumberParsed = parseInt(value);
        if (value.length === 10) {
            return birthNumberParsed % 11 === 0;
        }
        const yearOfBirth = parseInt(value.substring(0, 2));
        return yearOfBirth < 54;
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "validAccountPrefix", function (t: TFunction) {
    return this.test("validAccountPrefix", t("error.notValid"), (value: string): boolean => {
        if (!value) {
            return true;
        }

        const weights = [10, 5, 8, 4, 2, 1];
        const paddedPrefix = value.padStart(6, "0");

        // We need to multiply every digit from prefix with it's defined height. After
        // the multiplication we need to sum all multiplied digits and check modulo 11 to be 0.
        const weightedSum = paddedPrefix
            .split("")
            .map((digit, index) => Number(digit) * weights[index])
            .reduce((sum, value) => sum + value);

        return weightedSum > 0 && weightedSum % 11 === 0;
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "validAccountNumber", function (t: TFunction) {
    return this.test("validAccountNumber", t("error.notValid"), (value: string): boolean => {
        if (!value) {
            return true;
        }

        const weights = [6, 3, 7, 9, 10, 5, 8, 4, 2, 1];
        const paddedNumber = value.padStart(10, "0");

        // We need to multiply every digit from account number with it's defined height. After
        // the multiplication we need to sum all multiplied digits and check modulo 11 to be 0.
        const weightedSum = paddedNumber
            .split("")
            .map((digit, index) => Number(digit) * weights[index])
            .reduce((sum, value) => sum + value);

        return weightedSum > 0 && weightedSum % 11 === 0;
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "validIban", function (t: TFunction) {
    return this.test("validIban", t("error.notValid"), (value: string): boolean => {
        try {
            return iban.validateIBAN(value);
        } catch (e) {
            return false;
        }
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "validSkIban", function (t: TFunction) {
    return this.test("validSkIban", t("error.notValid"), (value: string): boolean => {
        try {
            iban.validateIBAN(value);
            return value.startsWith("SK");
        } catch (e) {
            // if iban is not valid, validation of sk iban is skipped
            return true;
        }
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "isDateSameOrBefore", function (t: TFunction, compareTo: Dayjs) {
    return this.test("isDateSameOrBefore", t("error.notValid"), (value: string): boolean => {
        if (!value) {
            return false;
        }
        return dayjs(value).isSameOrBefore(compareTo, "day");
    });
});

yup.addMethod<yup.StringSchema>(yup.string, "isDateSameOrAfter", function (t: TFunction, compareTo: Dayjs) {
    return this.test("isDateSameOrAfter", t("error.notValid"), (value: string): boolean => {
        if (!value) {
            return false;
        }
        return dayjs(value).isSameOrAfter(compareTo, "day");
    });
});
